import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  MILLI_UNITS,
  STOCK_UNITS,
  formatQuantity,
  paiseToRupees,
  parseQuantity,
  parseRupees,
  type OrderProductLine,
  type OrderProductLineInput,
  type Product,
  type StockUnit,
} from '@tailoring-crm/shared';
import { Button, Input, Label } from '@tailoring-crm/ui';
import { formatCurrency } from '../../lib/format.js';

/**
 * What the counter sells alongside — or instead of — the stitching: fabric by the metre, a
 * readymade blouse, a packet of buttons.
 *
 * A LINE OF THE ORDER, not of a garment, so it has none of a garment's fields: no measurements,
 * no due date, no tailor. Pick it from the shop's product list and its name, unit and price come
 * with it; or type it, for the one-off the list has never heard of. The price is always editable,
 * as a garment's is.
 *
 * Quantities are typed as a person says them — "2.5" metres — and turned into milli-units only
 * on the way out (`parseQuantity`), the representation the server and the stock ledger share.
 */

/** One row as the form holds it: everything as typed, so an emptied box is not a zero. */
export interface DraftProductLine {
  /** The catalogue product, or `''` for a line typed by hand. */
  productId: string;
  name: string;
  unit: StockUnit;
  /** In the line's own unit, as typed: "2.5". */
  quantity: string;
  /** Rupees per unit, as typed. */
  price: string;
}

export const EMPTY_PRODUCT_LINE: DraftProductLine = {
  productId: '',
  name: '',
  unit: 'piece',
  quantity: '1',
  price: '',
};

/** Garment work is booked as a garment, not sold over the counter, so the picker leaves it out. */
const COUNTER_CATEGORIES = new Set<Product['category']>([
  'fabric',
  'readymade',
  'accessory',
  'service',
  'other',
]);

function paiseOf(input: string): number {
  if (input.trim() === '') return 0;
  try {
    return parseRupees(input);
  } catch {
    return 0;
  }
}

function priceInputValue(paise: number | null): string {
  return paise === null ? '' : String(paiseToRupees(paise));
}

/** The line's quantity in milli-units, or 0 for anything that is not a quantity. */
function milliOf(line: DraftProductLine): number {
  const milli = parseQuantity(line.quantity);
  return milli !== null && milli > 0 ? milli : 0;
}

/** What one row comes to — the same arithmetic the server does in `productLinePaise`. */
export function draftProductLinePaise(line: DraftProductLine): number {
  return Math.round((milliOf(line) * paiseOf(line.price)) / MILLI_UNITS);
}

export function draftProductLinesPaise(lines: readonly DraftProductLine[]): number {
  return lines.reduce((sum, line) => sum + draftProductLinePaise(line), 0);
}

/** A row that can be sent: it names something and sells a positive amount of it. */
function isUsable(line: DraftProductLine): boolean {
  return (line.productId !== '' || line.name.trim() !== '') && milliOf(line) > 0;
}

export function usableProductLines(lines: readonly DraftProductLine[]): DraftProductLine[] {
  return lines.filter(isUsable);
}

/** The rows as the API takes them. Half-typed rows are left behind rather than refused. */
export function toProductLineInputs(lines: readonly DraftProductLine[]): OrderProductLineInput[] {
  return usableProductLines(lines).map((line) =>
    line.productId
      ? { productId: line.productId, quantity: milliOf(line), unitPricePaise: paiseOf(line.price) }
      : {
          name: line.name.trim(),
          unit: line.unit,
          quantity: milliOf(line),
          unitPricePaise: paiseOf(line.price),
        },
  );
}

/** An order's stored lines, back into rows the editor can change. */
export function fromProductLines(lines: readonly OrderProductLine[]): DraftProductLine[] {
  return lines.map((line) => ({
    productId: line.productId ?? '',
    name: line.name,
    unit: line.unit,
    quantity: formatQuantity(line.quantity, 'metre'),
    price: priceInputValue(line.unitPricePaise),
  }));
}

export interface ProductLinesEditorProps {
  lines: DraftProductLine[];
  onChange: (lines: DraftProductLine[]) => void;
  /** The shop's live product list. */
  products: readonly Product[];
  /** Prefixes every input id, so two editors on one screen do not collide. */
  idPrefix?: string;
}

export function ProductLinesEditor({
  lines,
  onChange,
  products,
  idPrefix = 'product-line',
}: ProductLinesEditorProps): ReactElement {
  const { t } = useTranslation();
  const counterProducts = products.filter((product) => COUNTER_CATEGORIES.has(product.category));

  const setLine = (index: number, patch: Partial<DraftProductLine>): void => {
    onChange(lines.map((line, position) => (position === index ? { ...line, ...patch } : line)));
  };

  /** Picking from the list brings its name, unit and price; the blank option types one in. */
  const chooseProduct = (index: number, productId: string): void => {
    const chosen = counterProducts.find((product) => product.id === productId);
    setLine(
      index,
      chosen
        ? {
            productId: chosen.id,
            name: chosen.name,
            unit: chosen.unit,
            price: priceInputValue(chosen.unitPricePaise),
          }
        : { productId: '', name: '' },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {lines.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
          {t('orders.productLines.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {lines.map((line, index) => {
            const id = `${idPrefix}-${index}`;
            return (
              <li key={index} className="flex flex-col gap-3 rounded-lg border border-border p-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`${id}-product`}>{t('orders.productLines.product')}</Label>
                    <select
                      id={`${id}-product`}
                      value={line.productId}
                      onChange={(event) => chooseProduct(index, event.target.value)}
                      className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                    >
                      <option value="">{t('orders.productLines.typeOwn')}</option>
                      {counterProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* A catalogue line already has its name and unit; only a typed one asks. */}
                  {line.productId === '' ? (
                    <div className="grid grid-cols-[1fr_auto] gap-3">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${id}-name`}>{t('orders.productLines.name')}</Label>
                        <Input
                          id={`${id}-name`}
                          value={line.name}
                          onChange={(event) => setLine(index, { name: event.target.value })}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${id}-unit`}>{t('orders.productLines.unit')}</Label>
                        <select
                          id={`${id}-unit`}
                          value={line.unit}
                          onChange={(event) =>
                            setLine(index, { unit: event.target.value as StockUnit })
                          }
                          className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                        >
                          {STOCK_UNITS.map((unit) => (
                            <option key={unit} value={unit}>
                              {t(`stock.unit.${unit}`)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`${id}-quantity`}>
                      {t('orders.productLines.quantity', {
                        unit: t(`sourcing.unit.${line.unit}`),
                      })}
                    </Label>
                    <Input
                      id={`${id}-quantity`}
                      inputMode="decimal"
                      value={line.quantity}
                      onChange={(event) => setLine(index, { quantity: event.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`${id}-price`}>
                      {t('orders.productLines.price', { unit: t(`sourcing.unit.${line.unit}`) })}
                    </Label>
                    <Input
                      id={`${id}-price`}
                      inputMode="decimal"
                      value={line.price}
                      onChange={(event) => setLine(index, { price: event.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-1 pb-2.5">
                    <span className="min-w-16 text-right text-sm font-semibold tabular-nums">
                      {formatCurrency(draftProductLinePaise(line))}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('orders.productLines.remove', { position: index + 1 })}
                      onClick={() => onChange(lines.filter((_, position) => position !== index))}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Button
        size="sm"
        variant="outline"
        className="self-start"
        onClick={() => onChange([...lines, { ...EMPTY_PRODUCT_LINE }])}
      >
        <Plus aria-hidden="true" />
        {t('orders.productLines.add')}
      </Button>
    </div>
  );
}
